import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  CircularProgress,
  Grid,
  Chip,
  Autocomplete,
  Card,
  Button,
} from '@mui/material';
import { RotateLeft as ResetIcon } from '@mui/icons-material';
import { adminAPI, locationAPI } from '../../services/api';

// Consistent text field styling
const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.5,
    bgcolor: 'background.paper',
    fontSize: '0.963rem',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'grey.300',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'primary.main',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.963rem',
  },
};

function Forms() {
  const navigate = useNavigate();
  const location = useLocation();

  const getInitialFormType = () => {
    if (location.pathname.includes('/forms/hbyc')) return 'hbyc';
    if (location.pathname.includes('/forms/hbnc')) return 'hbnc';
    if (location.pathname.includes('/forms/nrc')) return 'nrc';
    if (location.pathname.includes('/forms/sncu')) return 'sncu';
    return 'all';
  };

  const [forms, setForms] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [districtFilter, setDistrictFilter] = useState('');
  const [talukaFilter, setTalukaFilter] = useState('');
  const [ashaWorkerFilter, setAshaWorkerFilter] = useState('');
  const [formTypeFilter, setFormTypeFilter] = useState(getInitialFormType());
  const [loading, setLoading] = useState(true);
  const [districts, setDistricts] = useState([]);
  const [talukaOptions, setTalukaOptions] = useState([]);
  const [ashaWorkers, setAshaWorkers] = useState([]);

  useEffect(() => {
    if (location.pathname.includes('/forms/hbyc')) {
      setFormTypeFilter('hbyc');
    } else if (location.pathname.includes('/forms/hbnc')) {
      setFormTypeFilter('hbnc');
    } else if (location.pathname.includes('/forms/nrc')) {
      setFormTypeFilter('nrc');
    } else if (location.pathname.includes('/forms/sncu')) {
      setFormTypeFilter('sncu');
    } else {
      setFormTypeFilter('all');
    }
  }, [location.pathname]);

  useEffect(() => {
    fetchDistricts();
    fetchAshaWorkers();
  }, []);

  useEffect(() => {
    if (districtFilter) {
      const selected = districts.find(d => d._id === districtFilter);
      if (selected && selected.talukas) {
        setTalukaOptions(selected.talukas.map(t => ({
          label: t.name_mr,
          value: t._id
        })));
      } else {
        setTalukaOptions([]);
      }
    } else {
      setTalukaOptions([]);
      setTalukaFilter('');
    }
  }, [districtFilter, districts]);

  useEffect(() => {
    fetchForms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, districtFilter, talukaFilter, ashaWorkerFilter, formTypeFilter]);

  const fetchDistricts = async () => {
    try {
      const response = await locationAPI.getDistricts();
      setDistricts(response.data);
    } catch (err) {
      console.error('Failed to fetch districts:', err);
    }
  };

  const fetchAshaWorkers = async () => {
    try {
      let allWorkers = [];
      let skip = 0;
      const limit = 100;
      let hasMore = true;

      while (hasMore) {
        const response = await adminAPI.getUsers({
          skip,
          limit,
          role: 'ASHA_WORKER',
          is_active: true
        });
        const users = response.data.users || [];
        const workers = users.map(u => ({
          label: u.full_name,
          value: u.id
        }));
        allWorkers = [...allWorkers, ...workers];
        hasMore = users.length === limit && response.data.total > skip + limit;
        skip += limit;
      }
      setAshaWorkers(allWorkers);
    } catch (err) {
      console.error('Failed to fetch ASHA workers:', err);
    }
  };

  const fetchForms = async () => {
    setLoading(true);
    try {
      const params = {
        skip: page * rowsPerPage,
        limit: rowsPerPage,
      };
      if (search) params.search = search;
      if (districtFilter) params.district_id = districtFilter;
      if (talukaFilter) params.taluka_id = talukaFilter;
      if (ashaWorkerFilter) params.user_id = ashaWorkerFilter;

      let allForms = [];
      let totalCount = 0;

      if (formTypeFilter === 'hbnc') {
        const hbncResponse = await adminAPI.getHBNCForms(params);
        allForms = hbncResponse.data.forms.map(form => ({ ...form, type: 'HBNC' }));
        totalCount = hbncResponse.data.total;
      } else if (formTypeFilter === 'hbyc') {
        const hbycResponse = await adminAPI.getHBYCForms(params);
        allForms = hbycResponse.data.forms.map(form => ({ ...form, type: 'HBYC' }));
        totalCount = hbycResponse.data.total;
      } else if (formTypeFilter === 'nrc') {
        const nrcResponse = await adminAPI.getNRCForms(params);
        allForms = nrcResponse.data.forms.map(form => ({ ...form, type: 'NRC' }));
        totalCount = nrcResponse.data.total;
      } else if (formTypeFilter === 'sncu') {
        const sncuResponse = await adminAPI.getSNCUForms(params);
        allForms = sncuResponse.data.forms.map(form => ({ ...form, type: 'SNCU' }));
        totalCount = sncuResponse.data.total;
      } else {
        const [hbncResponse, hbycResponse, nrcResponse, sncuResponse] = await Promise.all([
          adminAPI.getHBNCForms(params),
          adminAPI.getHBYCForms(params),
          adminAPI.getNRCForms(params),
          adminAPI.getSNCUForms(params),
        ]);
        const hbncForms = hbncResponse.data.forms.map(form => ({ ...form, type: 'HBNC' }));
        const hbycForms = hbycResponse.data.forms.map(form => ({ ...form, type: 'HBYC' }));
        const nrcForms = nrcResponse.data.forms.map(form => ({ ...form, type: 'NRC' }));
        const sncuForms = sncuResponse.data.forms.map(form => ({ ...form, type: 'SNCU' }));
        allForms = [...hbncForms, ...hbycForms, ...nrcForms, ...sncuForms].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        totalCount = hbncResponse.data.total + hbycResponse.data.total + nrcResponse.data.total + sncuResponse.data.total;
      }

      setForms(allForms);
      setTotal(totalCount);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch forms:', err);
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleReset = () => {
    setSearch('');
    setDistrictFilter('');
    setTalukaFilter('');
    setAshaWorkerFilter('');
    setPage(0);
  };

  const formatDate = (dateString) => {
    const utcDateString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const date = new Date(utcDateString);
    return date.toLocaleString('mr-IN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Kolkata',
    });
  };

  const getFormTypeChipStyles = (type) => {
    if (type === 'HBNC') {
      return {
        bgcolor: '#FFF7ED',
        color: '#C2410C',
      };
    }
    if (type === 'NRC') {
      return {
        bgcolor: '#F3E5F5',
        color: '#7B1FA2',
      };
    }
    if (type === 'SNCU') {
      return {
        bgcolor: '#E0F7FA',
        color: '#00838F',
      };
    }
    return {
      bgcolor: '#EFF6FF',
      color: '#1D4ED8',
    };
  };

  return (
    <Box sx={{ maxWidth: 1400 }}>
      {/* Filters */}
      <Card
        sx={{
          p: 2.5,
          mb: 3,
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 2,
          boxShadow: 'none',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2.4}>
            <Autocomplete
              fullWidth
              size="small"
              options={districts.map(d => ({ label: d.name_mr, value: d._id }))}
              getOptionLabel={(option) => option.label}
              value={districts.map(d => ({ label: d.name_mr, value: d._id })).find(d => d.value === districtFilter) || null}
              onChange={(e, newValue) => setDistrictFilter(newValue?.value || '')}
              renderInput={(params) => (
                <TextField {...params} label="जिल्हा" sx={textFieldSx} />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Autocomplete
              fullWidth
              size="small"
              options={talukaOptions}
              getOptionLabel={(option) => option.label}
              value={talukaOptions.find(t => t.value === talukaFilter) || null}
              onChange={(e, newValue) => setTalukaFilter(newValue?.value || '')}
              disabled={!districtFilter}
              renderInput={(params) => (
                <TextField {...params} label="तालुका" sx={textFieldSx} />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Autocomplete
              fullWidth
              size="small"
              options={ashaWorkers}
              getOptionLabel={(option) => option.label}
              value={ashaWorkers.find(w => w.value === ashaWorkerFilter) || null}
              onChange={(e, newValue) => setAshaWorkerFilter(newValue?.value || '')}
              renderInput={(params) => (
                <TextField {...params} label="आशा कार्यकर्ता" sx={textFieldSx} />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              fullWidth
              label="शोधा"
              variant="outlined"
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={
                formTypeFilter === 'nrc'
                  ? 'ASHA संपर्क किंवा १४ अंकी ABHA'
                  : formTypeFilter === 'sncu'
                  ? 'बाळाचे नाव किंवा १४ अंकी ABHA'
                  : 'मोबाइल किंवा १४ अंकी ABHA'
              }
              helperText="१४ अंकी ABHA टाकल्यास त्या आईच्या याच फॉर्म प्रकारातील रेकॉर्ड सापडेल"
              sx={textFieldSx}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Button
              fullWidth
              variant="text"
              startIcon={<ResetIcon sx={{ fontSize: 20 }} />}
              onClick={handleReset}
              sx={{
                color: 'grey.600',
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  bgcolor: 'grey.100',
                },
              }}
            >
              रीसेट
            </Button>
          </Grid>
        </Grid>
      </Card>

      {/* Table */}
      <Card
        sx={{
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 2,
          boxShadow: 'none',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress size={32} sx={{ color: 'grey.400' }} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    {['प्रकार', 'आशा कार्यकर्ता', 'आईचे नाव', 'बालाचे नाव', 'जिल्हा', 'तालुका', 'मोबाइल', 'तारीख', 'स्थिती'].map((header) => (
                      <TableCell
                        key={header}
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.825rem',
                          color: 'grey.600',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          py: 1.5,
                          borderBottom: '1px solid',
                          borderColor: 'grey.200',
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {forms.map((form) => (
                    <TableRow
                      key={`${form.type}-${form.id}`}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'grey.50' },
                        '&:last-child td': { borderBottom: 0 },
                      }}
                      onClick={() => navigate(`/admin/forms/${form.type.toLowerCase()}/${form.id}`)}
                    >
                      <TableCell sx={{ py: 2 }}>
                        <Chip
                          label={form.type}
                          size="small"
                          sx={{
                            height: 24,
                            fontSize: '0.825rem',
                            fontWeight: 600,
                            border: 'none',
                            ...getFormTypeChipStyles(form.type),
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography sx={{ fontSize: '0.963rem', fontWeight: 500, color: 'grey.900' }}>
                          {form.ashaWorkerName}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography sx={{ fontSize: '0.894rem', color: 'grey.600' }}>
                          {form.type === 'HBNC'
                            ? form.formData?.motherNewborn?.motherName || '-'
                            : form.type === 'NRC'
                              ? form.formData?.childDetails?.motherName || '-'
                              : form.type === 'SNCU'
                                ? form.formData?.childInfo?.motherName || '-'
                                : form.formData?.childProfile?.motherName || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography sx={{ fontSize: '0.894rem', color: 'grey.600' }}>
                          {form.type === 'HBNC'
                            ? form.formData?.motherNewborn?.babyName || '-'
                            : form.type === 'NRC'
                              ? form.formData?.childDetails?.childName || '-'
                              : form.type === 'SNCU'
                                ? form.formData?.childInfo?.childName || '-'
                                : form.formData?.childProfile?.childName || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography sx={{ fontSize: '0.894rem', color: 'grey.600' }}>
                          {form.type === 'HBNC'
                            ? form.formData?.identification?.district || '-'
                            : form.type === 'NRC' || form.type === 'SNCU'
                              ? '-'
                              : form.formData?.childProfile?.district || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography sx={{ fontSize: '0.894rem', color: 'grey.600' }}>
                          {form.type === 'HBNC'
                            ? form.formData?.identification?.taluka || '-'
                            : form.type === 'NRC' || form.type === 'SNCU'
                              ? '-'
                              : form.formData?.childProfile?.taluka || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography sx={{ fontSize: '0.894rem', color: 'grey.600' }}>
                          {form.type === 'HBNC'
                            ? form.formData?.motherNewborn?.mobileNumber || '-'
                            : form.type === 'NRC'
                              ? form.formData?.ashaAnmDetails?.ashaContactNumber || '-'
                              : form.type === 'SNCU'
                                ? '-'
                                : form.formData?.childProfile?.motherMobile || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography sx={{ fontSize: '0.894rem', color: 'grey.500' }}>
                          {formatDate(form.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Chip
                          label={form.syncStatus === 'synced' ? 'समक्रमित' : 'प्रलंबित'}
                          size="small"
                          sx={{
                            height: 24,
                            fontSize: '0.825rem',
                            fontWeight: 600,
                            bgcolor: form.syncStatus === 'synced' ? 'success.lighter' : 'warning.lighter',
                            color: form.syncStatus === 'synced' ? 'success.dark' : 'warning.dark',
                            border: 'none',
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="प्रति पृष्ठ पंक्ती:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} पैकी ${count}`}
              sx={{
                borderTop: '1px solid',
                borderColor: 'grey.200',
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: '0.894rem',
                  color: 'grey.600',
                },
              }}
            />
          </>
        )}
      </Card>
    </Box>
  );
}

export default Forms;
